// ============================================================================
// TRAINING REPORT SERVICE - FIXED
// ============================================================================
// Handles saving, retrieving, and analyzing training reports.
// Now includes key hands storage for session review.
// ============================================================================

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { API_BASE } from '../../utils/apiBase';

// ============================================================================
// TYPES
// ============================================================================

export interface KeyHand {
  handNumber: number;
  position: string;
  holeCards: string[];
  board: string[];
  potSize: number;
  street: 'preflop' | 'flop' | 'turn' | 'river';
  userAction: string;
  userAmount: number;
  correctAction: string;
  correctAmount?: number;
  wasCorrect: boolean;
  isMistake: boolean;
  isGoodPlay: boolean;
  explanation: string;
  conceptsTested: string[];
}

export interface TrainingReport {
  reportId: string;
  sessionId: string;
  uid: string;
  username?: string;
  
  generatedAt: number;
  sessionDuration: number;
  
  overallScore: number;
  skillLevel: string;
  handsPlayed: number;
  decisionsAnalyzed: number;
  
  stats: {
    vpip: number;
    pfr: number;
    aggressionFactor: number;
    cbetFrequency: number;
    bluffFrequency?: number;
    wtsd?: number;
    wonAtShowdown?: number;
  };
  
  positionStats?: Record<string, any>;
  metrics?: Array<{
    name: string;
    score: number;
    feedback?: string;
    evaluations?: any[];
  }>;
  
  topStrengths: string[];
  topWeaknesses: string[];
  keyMistakes?: any[];
  bestPlays?: any[];
  recommendedDrills?: string[];
  
  glossaryTerms?: Record<string, any>;
  detailedExamples?: any[];
  nextStepsGuide?: string[];
  
  // NEW: Key hands for review
  keyHands?: KeyHand[];

  // The challenge this session was played under (challenge tables only)
  challenge?: {
    id: string;
    title: string;
    mission?: string;
    tips?: string[];
    skill?: string;
    concept?: string;
    goalType?: string;
    goalChips?: number;
    heroChips?: number;
    maxHands?: number;
  } | null;
}

export interface ReportSummary {
  reportId: string;
  generatedAt: number;
  handsPlayed: number;
  duration: number;
  overallScore: number;
  topStrength: string | null;
  topWeakness: string | null;
}

export interface TrainingProgress {
  uid: string;
  totalSessions: number;
  totalHandsPlayed: number;
  averageScore: number;
  bestScore: number;
  recentScores: number[];
  scoreImprovement: number;
  metricAverages: Record<string, number>;
  milestones: Array<{
    id: string;
    name: string;
    type: 'score' | 'hands' | 'sessions' | 'skill';
    description: string;
    unlockedAt: number;
  }>;
}

// ============================================================================
// HELPER: Deep clean undefined values
// ============================================================================

function deepClean(obj: any): any {
  if (obj === undefined) {
    return null;
  }
  
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj
      .filter(item => item !== undefined)
      .map(item => deepClean(item));
  }
  
  if (obj instanceof Timestamp) {
    return obj;
  }
  
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      const cleanedValue = deepClean(value);
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }
    }
  }
  return cleaned;
}

// ============================================================================
// SAVE REPORT
// ============================================================================

export async function saveTrainingReport(report: TrainingReport): Promise<void> {
  try {
    console.log(`💾 [TrainingReport] Saving report ${report.reportId}`);
    
    // Deep clean all undefined values
    const cleanedReport = deepClean({
      reportId: report.reportId,
      sessionId: report.sessionId,
      uid: report.uid,
      username: report.username || 'Player',
      generatedAt: report.generatedAt,
      sessionDuration: report.sessionDuration || 0,
      overallScore: report.overallScore || 0,
      skillLevel: report.skillLevel || 'beginner',
      handsPlayed: report.handsPlayed || 0,
      decisionsAnalyzed: report.decisionsAnalyzed || 0,
      stats: {
        vpip: report.stats?.vpip || 0,
        pfr: report.stats?.pfr || 0,
        aggressionFactor: report.stats?.aggressionFactor || 0,
        cbetFrequency: report.stats?.cbetFrequency || 0,
        bluffFrequency: report.stats?.bluffFrequency || 0,
        wtsd: report.stats?.wtsd || 0,
        wonAtShowdown: report.stats?.wonAtShowdown || 0,
      },
      positionStats: report.positionStats || {},
      metrics: (report.metrics || []).map(m => ({
        name: m.name || 'Unknown',
        score: m.score || 0,
        feedback: m.feedback || '',
        // Don't save evaluations - too big
      })),
      topStrengths: (report.topStrengths || []).slice(0, 5),
      topWeaknesses: (report.topWeaknesses || []).slice(0, 5),
      keyMistakes: (report.keyMistakes || []).slice(0, 10),
      bestPlays: (report.bestPlays || []).slice(0, 10),
      recommendedDrills: (report.recommendedDrills || []).slice(0, 5),
      keyHands: (report.keyHands || []).slice(0, 20),
      challenge: (report as any).challenge || null,
      challengeOutcome: (report as any).challengeOutcome || null,
      savedAt: Timestamp.now(),
    });
    
    const reportRef = doc(db, 'trainingReports', report.reportId);
    await setDoc(reportRef, cleanedReport);
    
    console.log(`✅ [TrainingReport] Saved report ${report.reportId}`);
  } catch (error) {
    console.error(`❌ [TrainingReport] Failed to save report:`, error);
    throw error;
  }
}

// ============================================================================
// GET SINGLE REPORT
// ============================================================================

export async function getTrainingReport(reportId: string): Promise<TrainingReport | null> {
  try {
    const reportRef = doc(db, 'trainingReports', reportId);
    const reportSnap = await getDoc(reportRef);
    
    if (!reportSnap.exists()) {
      return null;
    }
    
    const data = reportSnap.data();
    return {
      ...data,
      reportId: reportSnap.id,
      generatedAt: data.generatedAt?.toMillis?.() || data.generatedAt || Date.now(),
      sessionDuration: data.sessionDuration || 0,
    } as TrainingReport;
  } catch (error) {
    console.error('Failed to get training report:', error);
    return null;
  }
}

// ============================================================================
// GET USER REPORTS
// ============================================================================

export async function getUserTrainingReports(
  uid: string,
  maxReports: number = 20
): Promise<ReportSummary[]> {
  try {
    console.log(`📊 [TrainingReport] Fetching reports for ${uid}`);
    
    const reportsRef = collection(db, 'trainingReports');
    const q = query(
      reportsRef,
      where('uid', '==', uid),
      orderBy('generatedAt', 'desc'),
      limit(maxReports)
    );
    
    const snapshot = await getDocs(q);
    console.log(`📊 [TrainingReport] Found ${snapshot.docs.length} reports`);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        reportId: doc.id,
        generatedAt: data.generatedAt?.toMillis?.() || data.generatedAt || 0,
        handsPlayed: data.handsPlayed || 0,
        duration: data.sessionDuration || 0,
        overallScore: data.overallScore || 0,
        topStrength: data.topStrengths?.[0] || null,
        topWeakness: data.topWeaknesses?.[0] || null,
      };
    });
  } catch (error) {
    console.error('Failed to get user training reports:', error);
    return [];
  }
}

// ============================================================================
// GET PROGRESS ANALYTICS
// ============================================================================

export async function getProgressAnalytics(uid: string): Promise<{
  progress: TrainingProgress | null;
  recommendedFocus: string[];
}> {
  try {
    const reports = await getUserTrainingReports(uid, 50);
    
    if (reports.length === 0) {
      return { progress: null, recommendedFocus: [] };
    }
    
    // Load full reports for detailed analysis
    const fullReports: TrainingReport[] = [];
    for (const summary of reports.slice(0, 20)) {
      const full = await getTrainingReport(summary.reportId);
      if (full) fullReports.push(full);
    }
    
    const scores = fullReports.map(r => r.overallScore);
    const totalHands = fullReports.reduce((sum, r) => sum + r.handsPlayed, 0);
    
    // Calculate metric averages
    const metricAverages: Record<string, number> = {};
    const metricCounts: Record<string, number> = {};
    
    for (const report of fullReports) {
      for (const metric of (report.metrics || [])) {
        const key = metric.name.toLowerCase().replace(/\s+/g, '_');
        metricAverages[key] = (metricAverages[key] || 0) + metric.score;
        metricCounts[key] = (metricCounts[key] || 0) + 1;
      }
    }
    
    for (const key of Object.keys(metricAverages)) {
      metricAverages[key] = metricAverages[key] / metricCounts[key];
    }
    
    // Calculate improvement
    const recentScores = scores.slice(0, 5);
    const olderScores = scores.slice(-5);
    const recentAvg = recentScores.reduce((a, b) => a + b, 0) / recentScores.length;
    const olderAvg = olderScores.length > 0 ? olderScores.reduce((a, b) => a + b, 0) / olderScores.length : recentAvg;
    const improvement = olderAvg > 0 ? ((recentAvg - olderAvg) / olderAvg) * 100 : 0;
    
    // Generate milestones
    const milestones: TrainingProgress['milestones'] = [];
    
    if (fullReports.length >= 1) {
      milestones.push({
        id: 'first_session',
        name: 'First Steps',
        type: 'sessions',
        description: 'Completed your first training session',
        unlockedAt: fullReports[fullReports.length - 1].generatedAt,
      });
    }
    if (fullReports.length >= 10) {
      milestones.push({
        id: 'ten_sessions',
        name: 'Dedicated Learner',
        type: 'sessions',
        description: 'Completed 10 training sessions',
        unlockedAt: Date.now(),
      });
    }
    if (totalHands >= 100) {
      milestones.push({
        id: 'hundred_hands',
        name: 'Century',
        type: 'hands',
        description: 'Played 100 training hands',
        unlockedAt: Date.now(),
      });
    }
    if (totalHands >= 500) {
      milestones.push({
        id: 'five_hundred_hands',
        name: 'Grinder',
        type: 'hands',
        description: 'Played 500 training hands',
        unlockedAt: Date.now(),
      });
    }
    if (scores.some(s => s >= 8)) {
      milestones.push({
        id: 'great_session',
        name: 'Star Performance',
        type: 'score',
        description: 'Scored 8+ in a session',
        unlockedAt: Date.now(),
      });
    }
    if (scores.some(s => s >= 9)) {
      milestones.push({
        id: 'excellent_session',
        name: 'Near Perfect',
        type: 'score',
        description: 'Scored 9+ in a session',
        unlockedAt: Date.now(),
      });
    }
    
    // Generate recommended focus areas
    const weakAreas = Object.entries(metricAverages)
      .filter(([_, score]) => score < 7)
      .sort((a, b) => a[1] - b[1])
      .slice(0, 3);
    
    const recommendedFocus = weakAreas.map(([area, score]) => {
      const areaName = area.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return `Focus on ${areaName} (avg score: ${score.toFixed(1)})`;
    });
    
    if (recommendedFocus.length === 0) {
      recommendedFocus.push('Keep up the great work! Consider challenging harder bots.');
    }
    
    const progress: TrainingProgress = {
      uid,
      totalSessions: fullReports.length,
      totalHandsPlayed: totalHands,
      averageScore: scores.reduce((a, b) => a + b, 0) / scores.length,
      bestScore: Math.max(...scores),
      recentScores: scores.slice(0, 10),
      scoreImprovement: improvement,
      metricAverages,
      milestones,
    };
    
    return { progress, recommendedFocus };
  } catch (error) {
    console.error('Failed to get progress analytics:', error);
    return { progress: null, recommendedFocus: [] };
  }
}

// ============================================================================
// GET KEY HANDS FOR REVIEW
// ============================================================================

export async function getKeyHandsForReview(
  uid: string,
  reportId?: string
): Promise<KeyHand[]> {
  try {
    if (reportId) {
      // Get key hands from specific report
      const report = await getTrainingReport(reportId);
      return report?.keyHands || [];
    }
    
    // Get key hands from recent reports
    const reports = await getUserTrainingReports(uid, 5);
    const allKeyHands: KeyHand[] = [];
    
    for (const summary of reports) {
      const report = await getTrainingReport(summary.reportId);
      if (report?.keyHands) {
        allKeyHands.push(...report.keyHands);
      }
    }
    
    // Return most interesting hands (mistakes and good plays)
    return allKeyHands
      .sort((a, b) => {
        // Prioritize mistakes, then good plays
        if (a.isMistake && !b.isMistake) return -1;
        if (!a.isMistake && b.isMistake) return 1;
        if (a.isGoodPlay && !b.isGoodPlay) return -1;
        return 0;
      })
      .slice(0, 20);
  } catch (error) {
    console.error('Failed to get key hands for review:', error);
    return [];
  }
}

// ============================================================================
// RETENTION & DELETE FUNCTIONS (API-based)
// ============================================================================

export const TRAINING_RETENTION = {
  MAX_REPORTS_PER_USER: 50,
  AUTO_DELETE_AFTER_DAYS: 180,
  WARN_AT_REPORTS: 40,
};

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

/**
 * Delete a single training report via API
 */
export async function deleteTrainingReport(reportId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE}/deleteTrainingReport?reportId=${reportId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await response.json();
    return { success: data.success, error: data.error };
  } catch (error) {
    console.error('Failed to delete training report:', error);
    return { success: false, error: 'Failed to delete report' };
  }
}

/**
 * Delete multiple training reports via API
 */
export async function deleteMultipleReports(reportIds: string[]): Promise<{ success: boolean; deleted: number; errors: string[] }> {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE}/deleteMultipleTrainingReports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ reportIds }),
    });
    const data = await response.json();
    return { success: data.success, deleted: data.deleted || 0, errors: data.errors || [] };
  } catch (error) {
    console.error('Failed to delete multiple reports:', error);
    return { success: false, deleted: 0, errors: ['Failed to delete reports'] };
  }
}

/**
 * Get retention status via API
 */
export async function getRetentionStatus(): Promise<{
  totalReports: number;
  maxReports: number;
  isAtLimit: boolean;
  isNearLimit: boolean;
  oldestReportDate: number | null;
  reportsOverRetention: number;
  recommendedAction: string | null;
}> {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE}/getTrainingRetentionStatus`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await response.json();
    return {
      totalReports: data.totalReports || 0,
      maxReports: data.maxReports || TRAINING_RETENTION.MAX_REPORTS_PER_USER,
      isAtLimit: data.isAtLimit || false,
      isNearLimit: data.isNearLimit || false,
      oldestReportDate: data.oldestReportDate || null,
      reportsOverRetention: data.reportsOverRetention || 0,
      recommendedAction: data.recommendedAction || null,
    };
  } catch (error) {
    console.error('Failed to get retention status:', error);
    return {
      totalReports: 0,
      maxReports: TRAINING_RETENTION.MAX_REPORTS_PER_USER,
      isAtLimit: false,
      isNearLimit: false,
      oldestReportDate: null,
      reportsOverRetention: 0,
      recommendedAction: null,
    };
  }
}

/**
 * Enforce retention limits via API - call before saving new reports
 */
export async function enforceRetentionLimits(): Promise<{
  deletedCount: number;
  remainingSlots: number;
}> {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE}/enforceTrainingRetention`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await response.json();
    return {
      deletedCount: data.deletedCount || 0,
      remainingSlots: data.remainingSlots || 0,
    };
  } catch (error) {
    console.error('Failed to enforce retention limits:', error);
    return { deletedCount: 0, remainingSlots: 0 };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  saveTrainingReport,
  getTrainingReport,
  getUserTrainingReports,
  getProgressAnalytics,
  getKeyHandsForReview,
  deleteTrainingReport,
  deleteMultipleReports,
  getRetentionStatus,
  enforceRetentionLimits,
  TRAINING_RETENTION,
};